import { ConnectProto } from "@racoon/api";
import { matchesAnySearch } from "@racoon/lib";
import type { CgRouteIndex } from "./cascade-rows";

/** An unset yes/no filter is the empty string. */
export type YesNo = "" | "yes" | "no";

/**
 * The filters that narrow the list, status aside. Status is not one of them:
 * each tab is counted under the same filters, so status is applied last, by
 * itself.
 */
export type LocationCriteria = {
  /** The provider's pickup location type id; "0" keeps locations with none. */
  typeId: string;
  customerId: string;
  containerTypeId: string;
  appAccess: YesNo;
  search: string;
  routeGroupId: string;
  truckId: string;
  /** "yes" keeps only locations with a container group that is in no route group. */
  withoutRouteGroup: "" | "yes";
};

const containerGroupsOf = (location: ConnectProto.PickupLocation) => location.containerGroups ?? [];

/** A contact phone on a container group is what the «Приложение» column reads as access. */
const hasAppAccess = (location: ConnectProto.PickupLocation) =>
  containerGroupsOf(location).some((cg) => !!cg.primaryContact?.phone);

/** The route groups and trucks a container group is bound to, if any. */
const routeOf = (routeIndex: CgRouteIndex, cg: ConnectProto.ContainerGroup) =>
  routeIndex.byCg.get(String(cg.id));

/**
 * Truck, "without route group" and app access describe relations
 * ListPickupLocations cannot express — the route groups' trucks, the absence of
 * a binding, the container groups' contacts. While one is set the list cannot
 * be paged on the server and is narrowed client-side instead.
 */
export const needsLocalFiltering = (filters: LocationCriteria) =>
  Boolean(filters.truckId || filters.withoutRouteGroup || filters.appAccess);

/**
 * The filters the request cannot carry (see `needsLocalFiltering`), applied to
 * one location. Every other filter travels with the request.
 */
export const matchesLocalCriteria = (
  location: ConnectProto.PickupLocation,
  filters: LocationCriteria,
  routeIndex: CgRouteIndex,
): boolean => {
  const { appAccess, truckId, withoutRouteGroup } = filters;
  const groups = containerGroupsOf(location);

  if (appAccess && hasAppAccess(location) !== (appAccess === "yes")) return false;

  if (truckId && !groups.some((cg) => routeOf(routeIndex, cg)?.truckIds.includes(truckId)))
    return false;

  // A location with one group still to bind is what the dispatcher is looking for, even
  // when its other groups are bound.
  if (
    withoutRouteGroup &&
    !groups.some((cg) => (routeOf(routeIndex, cg)?.groupIds.length ?? 0) === 0)
  )
    return false;

  return true;
};

/**
 * The URL keys of the filters that would hide `location`, so following a deep link to
 * it can clear just those. The search is matched against what the server searches —
 * name, address, the container groups' customers — and cleared when none of it matches.
 */
export const filtersExcluding = (
  location: ConnectProto.PickupLocation,
  filters: LocationCriteria,
  routeIndex: CgRouteIndex,
): string[] => {
  const groups = containerGroupsOf(location);
  const keys: string[] = [];

  // No type arrives as a missing field, not 0n.
  if (filters.typeId && String(location.pickupLocationTypeId ?? 0) !== filters.typeId)
    keys.push("pickup_location_type_id");
  if (
    filters.customerId &&
    !groups.some((cg) => String(cg.ownerOrganization?.id) === filters.customerId)
  )
    keys.push("customer_id");
  if (
    filters.containerTypeId &&
    !groups.some((cg) => String(cg.containerType?.id) === filters.containerTypeId)
  )
    keys.push("container_type_id");
  if (
    filters.routeGroupId &&
    !groups.some((cg) => routeOf(routeIndex, cg)?.groupIds.includes(filters.routeGroupId))
  )
    keys.push("route_group_id");
  if (
    !matchesAnySearch(
      [location.name, location.address, ...groups.map((cg) => cg.ownerOrganization?.name)],
      filters.search,
    )
  )
    keys.push("search");
  const only = (key: "truckId" | "appAccess" | "withoutRouteGroup") =>
    matchesLocalCriteria(
      location,
      { ...filters, truckId: "", appAccess: "", withoutRouteGroup: "", [key]: filters[key] },
      routeIndex,
    );
  if (!only("truckId")) keys.push("truck_id");
  if (!only("appAccess")) keys.push("app_access");
  if (!only("withoutRouteGroup")) keys.push("without_route_group");
  return keys;
};
